import styled from '@emotion/styled'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { tokens } from '../../theme.js'
import { MutedText, Spinner, plural } from '../styles/index.js'
import { LEGISLATIONS } from './PrepareDialog.js'

const colors = tokens.colors
const fontFamily = tokens.fonts.body

const Card = styled.div`
  border: 1px solid ${colors.status.loading};
  border-radius: 12px;
  padding: 14px 20px 12px;
  margin-bottom: 16px;
  background: ${colors.white};
`
const Head = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`
const Title = styled.p`
  margin: 0;
  font: 600 15px/1.35 ${fontFamily};
  color: ${colors.text.body};
`
const Steps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 10px;
`
const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 22px;
  font: 400 13px/1.3 ${fontFamily};
  color: ${colors.text.body};
  & > .MuiSvgIcon-root,
  & > .MuiCircularProgress-root {
    flex: 0 0 auto;
  }
  & > b {
    font-weight: 500;
    white-space: nowrap;
  }
  & > .detail {
    color: ${colors.text.muted};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
`

function StepIcon({ status }) {
  if (status === 'DONE') return <CheckCircleOutlineIcon sx={{ fontSize: 16, color: colors.status.ready }} />
  if (status === 'ERROR') return <ErrorOutlineIcon sx={{ fontSize: 16, color: colors.error }} />
  if (status === 'RUNNING') return <Spinner size={16} />
  return <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: colors.text.muted }} />
}
const seconds = (a, b) => `${Math.max(1, Math.round((new Date(b) - new Date(a)) / 1000))} s`
const timeLabel = t => new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

function prepareSteps({ op, combine, fileIds, names }) {
  const nameOf = id => names[id] || 'file'
  const children = op?.children ?? []
  const steps = children.map(ch => {
    const files = (ch.input?.fileIds ?? []).map(nameOf)
    if (ch.kind === 'COMBINE') {
      const detail =
        ch.status === 'DONE'
          ? `${plural(files.length, 'file')} → ${names[ch.input?.combinedFileId] ?? ch.result?.name ?? 'combined.pdf'} · ${plural(ch.result?.pages ?? 0, 'page')} · ${seconds(ch.createdAt, ch.updatedAt)}`
          : ch.status === 'ERROR'
            ? ch.result?.error || 'failed'
            : `${plural(files.length, 'file')} → one PDF, in the displayed order · ${ch.status === 'RUNNING' ? 'running' : 'queued'}`
      return { id: ch.id, kind: 'COMBINE', label: 'Combine', status: ch.status, detail, fileName: null }
    }
    const boxes = (ch.result?.outputs ?? []).reduce((n, o) => n + (o.suggestions?.length ?? 0), 0)
    const failed = (ch.result?.failed ?? []).map(f => `${nameOf(f.fileId)}: ${f.error}`).join('; ')
    const detail =
      ch.status === 'DONE'
        ? `${files.join(', ')} · ${boxes} ${boxes === 1 ? 'box' : 'boxes'} · ${seconds(ch.createdAt, ch.updatedAt)}`
        : ch.status === 'ERROR'
          ? failed || ch.result?.error || 'failed'
          : `${files.join(', ')} · ${ch.status === 'RUNNING' ? 'running' : 'queued'}`
    return { id: ch.id, kind: 'SUGGEST', label: 'Suggest redactions', status: ch.status, detail, fileName: files[0] || null }
  })
  if (combine && !steps.some(s => s.kind === 'SUGGEST'))
    steps.push({
      id: 'suggest-next',
      kind: 'SUGGEST',
      label: 'Suggest redactions',
      status: 'QUEUED',
      detail: 'the combined PDF · queued',
      fileName: null
    })
  if (!combine && !steps.length)
    for (const id of fileIds)
      steps.push({ id, kind: 'SUGGEST', label: 'Suggest redactions', status: 'QUEUED', detail: `${nameOf(id)} · queued`, fileName: nameOf(id) })
  return steps
}

export function OperationCard({ prepare, names }) {
  const { op, combine, fileIds, legislation, startedAt } = prepare
  const steps = prepareSteps({ op, combine, fileIds, names })
  const done = steps.filter(s => s.status === 'DONE' || s.status === 'ERROR').length
  const active = steps.filter(s => s.status === 'RUNNING').length
  const current = Math.min(done + 1, steps.length)
  const progress = combine
    ? `step ${current} of ${steps.length}`
    : active > 1
      ? `${active} steps running · ${done} of ${steps.length} done`
      : `${done} of ${steps.length} files done`
  const law = LEGISLATIONS.find(l => l.id === legislation)?.label ?? legislation
  return (
    <Card data-testid="prepare-card" data-status={op?.status ?? 'RUNNING'} data-done={done} data-steps={steps.length}>
      <Head>
        <Spinner size={16} style={{ marginTop: 3 }} />
        <div style={{ minWidth: 0 }}>
          <Title data-testid="prepare-card-title">Preparing {plural(fileIds.length, 'file')} for redaction</Title>
          <MutedText style={{ marginTop: 2 }} data-testid="prepare-card-meta">
            Started {timeLabel(startedAt)} · {progress} · {combine ? 'combined output' : 'separate outputs'} · {law}
          </MutedText>
        </div>
      </Head>
      <Steps>
        {steps.map(s => (
          <Step key={s.id} data-testid="prepare-step" data-kind={s.kind} data-status={s.status} data-file-name={s.fileName || undefined}>
            <StepIcon status={s.status} />
            <b>{s.label}</b>
            <span className="detail">{s.detail}</span>
          </Step>
        ))}
      </Steps>
    </Card>
  )
}
